import { DSA_INDEXER_BYTES, DSV4_FP8_BYTES, SPARSE_MLA_FP8_BYTES, convState, csa2State, dsv4State, gdnState, gqa, kdaState, mamba2State, mla, type KvGroup } from "./core.ts";
import type { HwFormat } from "./hardware.ts";
import type { ModelPreset } from "./models.ts";

// ── Import depuis Hugging Face ──────────────────────────────────────────────
// Même physique que les presets, mais lue dans config.json. Le nombre de
// paramètres vient des métadonnées safetensors de l'API, la taille native de la
// somme des fichiers. Une couche de type inconnu est comptée en attention
// complète : on surestime plutôt que de promettre une place qui n'existe pas.

export type HfConfig = {
  text_config?: HfConfig;
  llm_config?: HfConfig;              // NVIDIA Nemotron « omni » (Super VL, Bolt) : le modèle de langue
  tie_word_embeddings?: boolean;
  tie_embedding?: boolean;            // LFM2
  num_hidden_layers: number;
  hidden_size: number;
  num_attention_heads: number;
  vocab_size: number;
  // Noms utilisés par d'autres familles (GPT-2/BLOOM, ChatGLM, Falcon…)
  n_layer?: number; num_layers?: number; n_layers?: number;
  n_embd?: number; d_model?: number;
  n_head?: number; num_heads?: number;
  num_kv_heads?: number; multi_query_group_num?: number;
  padded_vocab_size?: number;
  num_key_value_heads?: number;
  head_dim?: number;
  max_position_embeddings?: number;
  layer_types?: string[];
  sliding_window?: number | null;
  sliding_window_pattern?: number;    // Gemma 3 : une couche globale toutes les N, les autres en fenêtre glissante
  global_head_dim?: number;
  num_global_key_value_heads?: number;
  hybrid_override_pattern?: string;
  layers_block_type?: string[];      // Nemotron-H récents : le même motif en liste (« mamba », « moe », « attention », « mlp »)
  mamba_num_heads?: number; mamba_head_dim?: number; ssm_state_size?: number; n_groups?: number; conv_kernel?: number;
  linear_num_key_heads?: number; linear_num_value_heads?: number;
  linear_key_head_dim?: number; linear_value_head_dim?: number; linear_conv_kernel_dim?: number;
  linear_attn_config?: { full_attn_layers: number[]; num_heads: number; head_dim: number; short_conv_kernel_size: number };
  kv_lora_rank?: number;
  qk_rope_head_dim?: number;
  index_head_dim?: number;
  indexer_types?: string[];
  compress_ratios?: number[];
  kv_source_layer_ids?: number[];     // DeepSeek V4.1 : couches qui écrivent le KV partagé
  index_source_layer_ids?: number[];
  engram_num_embeddings?: number[];   // DeepSeek V4.1 : tables Engram (lues par token, peu actives)
  engram_head_dim?: number;
  dspark_n_routed_experts?: number;   // DeepSeek V4.1 : experts des couches MTP (DSpark)
  hybrid_layer_pattern?: number[];    // MiMo-V2.6 : 0 = attention globale, 1 = fenêtre glissante
  v_head_dim?: number;                // valeurs plus petites que les clés (MiMo : 128 contre 192)
  swa_head_dim?: number; swa_v_head_dim?: number; swa_num_key_value_heads?: number;
  moe_layer_freq?: number | number[]; // MiMo : 1 = couche MoE, 0 = couche dense
  mlp_layer_types?: string[];         // Mellum : « sparse » (MoE) ou « dense » par couche
  num_dense_layers?: number;          // LFM2 MoE : couches denses en tête
  conv_L_cache?: number; conv_dim?: number; // LFM2 : convolutions courtes (couches « conv »)
  index_kpool?: number;               // GLM-5.3-Flash : clés d'indexeur regroupées par blocs
  index_kpool_compress?: boolean;
  indexer_head_dim?: number;          // Qwen3.8-Flash-Next (QSA) : clés d'indexeur par bloc
  indexer_kv_heads?: number;
  indexer_compress_ratio?: number;
  ngram_vocab_size_base?: number;     // Qwen3.8-Flash-Next : tables n-gram (lues par token)
  ple_embed_dim?: number;
  model_type?: string;
  rope_scaling?: Record<string, unknown> | null;
  layer_group_size?: number;          // Ling 3.x (bailing_hybrid) : une couche MLA par groupe, les autres en KDA
  short_conv_kernel_size?: number;
  num_attention_groups?: number;      // Step-5 : têtes KV (GQA)
  moe_num_experts?: number;           // Step-5 : experts routés, et les couches qui en ont
  moe_layer_list?: number[];
  sparse_config?: { enabled?: boolean; compression_method?: string; proxy_dim?: number; region_block_size?: number };
  // MiniMax M3 (MiniMax Sparse Attention) : une clé d'index par token sur les couches où sparse_attention_freq vaut 1
  sparse_attention_config?: { use_sparse_attention?: boolean; sparse_index_dim?: number; sparse_attention_freq?: number[] };
  // MoE : experts, routage, couches denses ou MTP (pour les paramètres actifs)
  n_routed_experts?: number; num_experts?: number | null; num_local_experts?: number;
  num_experts_per_tok?: number; experts_per_token?: number; num_experts_per_token?: number; top_k_experts?: number;
  moe_intermediate_size?: number | null; intermediate_size?: number;
  expert_hidden_dim?: number;         // Hy-Memory (hy_v3) : largeur d'un expert ; son moe_intermediate_size (le double) ne sert pas
  first_k_dense_replace?: number; mlp_only_layers?: number[]; interleave_moe_layer_step?: number;
  num_nextn_predict_layers?: number; mtp_num_hidden_layers?: number; mtp_hybrid_override_pattern?: string;
  mlp_hidden_act?: string; moe_latent_size?: number; routed_expert_hidden_size?: number;
  full_attention_interval?: number;   // Qwen3-Next : une couche complète toutes les N
  attention_chunk_size?: number;      // Llama 4 : attention par blocs
  no_rope_layers?: number[];          // Llama 4 : 1 = couche par blocs, 0 = globale
  quantization_config?: {
    quant_method?: string;
    quant_algo?: string;          // NVIDIA ModelOpt
    kv_cache_quant_algo?: string; // NVIDIA ModelOpt
    format?: string;              // compressed-tensors
    bits?: number;                // GPTQ / AWQ
    config_groups?: Record<string, { weights?: { num_bits?: number; type?: string } }>;
    kv_cache_scheme?: { num_bits?: number; type?: string } | null;
    store_dtype?: string;         // MiMo-V2.6 : experts stockés en MXFP4, le reste en FP8
  };
};

export type HfApi = { safetensors?: { total?: number; parameters?: Record<string, number> }; siblings?: { rfilename: string; size?: number }[] };

/** "org/modèle" depuis un lien huggingface.co / hf.co ou un id brut ; null sinon. */
export function parseHfRepo(input: string): string | null {
  const path = input.trim().replace(/^(https?:\/\/)?(www\.)?(huggingface\.co|hf\.co)\//i, "");
  const m = path.match(/^([\w.-]+)\/([\w.-]+)(?:[/?#].*)?$/);
  // Règles de Hugging Face (validate_repo_id) : 96 caractères au plus, ni « .. » ni « -- », pas de « . » ou
  // « - » au début ou à la fin. « ../.. » partait sinon en requête vers d'autres chemins du site.
  const valid = (s: string) => s.length <= 96 && !/\.\.|--|^[.-]|[.-]$/.test(s);
  return m && valid(m[1]) && valid(m[2]) ? `${m[1]}/${m[2]}` : null;
}

/** Format réel d'un checkpoint quantifié, d'après son quantization_config. */
function quantFormat(q: NonNullable<HfConfig["quantization_config"]>): { label: string; needs?: HwFormat } {
  const method = (q.quant_method ?? "").toLowerCase();
  const algo = (q.quant_algo ?? "").toUpperCase();
  const w = Object.values(q.config_groups ?? {})[0]?.weights; // compressed-tensors
  const bits = w?.num_bits ?? q.bits;
  if (method === "mxfp4") return { label: "MXFP4", needs: "fp4" };
  if (q.store_dtype === "mxfp4") return { label: "MXFP4 experts + FP8", needs: "fp4" };
  if (algo === "NVFP4" || q.format === "nvfp4-pack-quantized" || (w?.type === "float" && bits === 4)) return { label: "NVFP4", needs: "fp4" };
  if (method === "fp8" || algo === "FP8" || (w?.type === "float" && bits === 8)) return { label: "FP8", needs: "fp8" };
  if (bits) return { label: `INT${bits}${method === "gptq" || method === "awq" ? ` ${method.toUpperCase()}` : ""}` };
  return { label: method || "Quantized" };
}

/**
 * Paramètres actifs d'un MoE (vitesse de génération) : ceux du nom quand il les donne
 * (« 35B-A3B »), sinon le total moins les experts non routés de chaque couche MoE, et
 * tous ceux des couches MTP (inactives hors décodage spéculatif). Un peu au-dessus des
 * chiffres officiels, qui excluent souvent embeddings et vision : de 1 à 30 % sur les
 * presets ci-dessus. undefined pour un modèle dense.
 */
function moeActiveParams(repo: string, c: HfConfig, L: number, hidden: number, params: number): number | undefined {
  const experts = c.n_routed_experts ?? c.num_experts ?? c.num_local_experts ?? c.moe_num_experts;
  const topK = c.num_experts_per_tok ?? c.experts_per_token ?? c.num_experts_per_token ?? c.top_k_experts;
  const inter = c.expert_hidden_dim ?? c.moe_intermediate_size ?? c.intermediate_size;
  if (!experts || !topK || !inter || experts <= topK) return undefined;
  const named = repo.match(/[-_]A(\d+(?:\.\d+)?)B(?![a-z])/i);
  if (named) return Number(named[1]);
  const countE = (pattern?: string) => [...(pattern ?? "")].filter((x) => x === "E").length;
  const moeLayers = c.hybrid_override_pattern
    ? countE(c.hybrid_override_pattern)
    : c.layers_block_type
      ? c.layers_block_type.slice(0, L).filter((t) => t === "moe").length
    : c.mlp_layer_types
      ? c.mlp_layer_types.slice(0, L).filter((t) => t === "sparse").length
      : Array.isArray(c.moe_layer_freq)
        ? c.moe_layer_freq.slice(0, L).filter(Boolean).length
      : Array.isArray(c.moe_layer_list)
        ? c.moe_layer_list.filter((l) => l < L).length
        : Math.ceil((L - (c.first_k_dense_replace ?? c.num_dense_layers ?? 0) - (c.mlp_only_layers?.length ?? 0)) / (c.interleave_moe_layer_step ?? 1));
  const mtp = c.num_nextn_predict_layers ?? c.mtp_num_hidden_layers ?? countE(c.mtp_hybrid_override_pattern);
  // Tables Engram (DeepSeek) et n-gram (Qwen) : quelques lignes lues par token, comptées inactives.
  const engram = (c.engram_num_embeddings ?? []).reduce((a, b) => a + b, 0) * (c.engram_head_dim ?? 0)
    + (c.ngram_vocab_size_base ?? 0) * (c.ple_embed_dim ?? 0);
  // Expert : 3 matrices (SwiGLU), 2 en ReLU² (Nemotron) ; largeur réduite en « latent MoE ».
  const expert = (/relu2/.test(c.mlp_hidden_act ?? "") ? 2 : 3) * (c.moe_latent_size ?? c.routed_expert_hidden_size ?? hidden) * inter;
  const routed = params - engram / 1e9 - ((experts - topK) * moeLayers * expert) / 1e9;
  // Couches MTP sans experts quand la liste des couches MoE ne les compte pas (Step-5 : 3 couches MTP denses,
  // model.safetensors.index.json du dépôt).
  const mtpMoe = Array.isArray(c.moe_layer_list) ? c.moe_layer_list.filter((l) => l >= L).length : mtp;
  const withMtp = routed - ((c.dspark_n_routed_experts ?? experts) * mtpMoe * expert) / 1e9;
  // MTP absent du checkpoint (MiMo : brouillon dans un dossier à part) : rien à retirer.
  const active = withMtp > 0 ? withMtp : routed;
  if (!(active > 0 && active < params)) return undefined;
  return active >= 10 ? Math.round(active) : Math.round(active * 10) / 10;
}

/** Noms propres à un modèle pour des couches connues.
 *  Agnes-3.0-Flash (Agnes AI) : les couches de Qwen3.5 renommées. « delta » est l'attention
 *  linéaire Gated DeltaNet (mêmes champs linear_*), « global » l'attention complète, une couche
 *  sur 4 (global_attention_interval). Source : config.json et modeling_agnes.py du dépôt. */
const LAYER_ALIASES: Record<string, string> = {
  deepseek_sparse_attention: "full_attention",
  agnes_global_attention: "full_attention",
  agnes_delta_attention: "linear_attention",
};

export function presetFromHf(repo: string, raw: HfConfig, api: HfApi): ModelPreset {
  const c = raw.text_config ?? raw.llm_config ?? raw;
  // Nemotron 3.5 (llm_config) : pas de num_hidden_layers, une entrée de layers_block_type par couche.
  const L = c.num_hidden_layers ?? c.n_layer ?? c.num_layers ?? c.n_layers ?? c.layers_block_type?.length;
  const hidden = c.hidden_size ?? c.n_embd ?? c.d_model;
  const heads = c.num_attention_heads ?? c.n_head ?? c.num_heads;
  if (!(L > 0 && hidden > 0 && heads > 0)) throw new Error("This config.json has no layer count, hidden size or attention heads that the calculator recognizes.");
  // Paramètres des fichiers. « total » vient parfois des métadonnées de l'index (total_parameters),
  // que transformers calcule sans les couches qu'il ne charge pas : 31,58 G pour 32,91 G dans les
  // fichiers de Nemotron 3.5 Lightning, sa couche MTP oubliée puis retirée une seconde fois du poids.
  const stored = Object.values(api.safetensors?.parameters ?? {}).reduce((a, b) => a + b, 0);
  const params = Math.max(api.safetensors?.total ?? 0, stored) / 1e9;
  if (!(params > 0)) throw new Error("This repo has no safetensors weights, so its parameter count is unknown.");

  const kvHeads = c.num_key_value_heads ?? c.num_kv_heads ?? c.multi_query_group_num ?? c.num_attention_groups ?? heads;
  const headDim = c.head_dim ?? hidden / heads;
  // Ling 3.x (bailing_hybrid, modeling_bailing_moe_v3.py du dépôt) : une couche MLA toutes les layer_group_size, et
  // celles au-delà du dernier groupe complet ; les autres en Kimi Delta Attention, aux têtes et à la largeur de
  // l'attention. Décrit comme le linear_attn_config de Kimi Linear (couches comptées à partir de 1).
  const group = c.layer_group_size;
  const lac = c.linear_attn_config ?? (c.model_type === "bailing_hybrid" && group
    ? {
      full_attn_layers: Array.from({ length: L }, (_, i) => i).filter((i) => (i + 1) % group === 0 || i >= Math.floor(L / group) * group).map((i) => i + 1),
      num_heads: heads, head_dim: headDim, short_conv_kernel_size: c.short_conv_kernel_size ?? 4,
    }
    : undefined);

  // Type de chaque couche. "none" : MLP / MoE seuls (Nemotron), sans cache.
  // GLM-5.3-Flash : « deepseek_sparse_attention » est une couche d'attention complète (MLA),
  // et ses couches linéaires sont des Kimi Delta Attention (décrites par linear_attn_config).
  const types: string[] =
    c.layer_types?.slice(0, L).map((t) => {
      const a = LAYER_ALIASES[t] ?? t;
      return lac && a === "linear_attention" ? "kda" : a;
    }) ??
    // Qwen3-Next : pas de layer_types, une couche d'attention complète toutes les N.
    (c.full_attention_interval && c.linear_num_value_heads
      ? Array.from({ length: L }, (_, i) => ((i + 1) % c.full_attention_interval! === 0 ? "full_attention" : "linear_attention"))
      : null) ??
    // MiMo-V2.6 : motif 0 (attention globale) / 1 (fenêtre glissante).
    c.hybrid_layer_pattern?.slice(0, L).map((x) => (x ? "sliding_attention" : "full_attention")) ??
    // Llama 4 : couches « RoPE » en attention par blocs (le cache ne garde qu'un bloc,
    // comme une fenêtre glissante dans vLLM), couches NoPE en attention globale.
    (c.attention_chunk_size && c.no_rope_layers?.length === L
      ? c.no_rope_layers.map((r) => (r ? "sliding_attention" : "full_attention"))
      : null) ??
    (c.hybrid_override_pattern
      ? [...c.hybrid_override_pattern].map((ch) => (ch === "*" ? "full_attention" : ch === "M" ? "mamba" : "none"))
      : c.layers_block_type
        ? c.layers_block_type.slice(0, L).map((t) => (t === "attention" ? "full_attention" : t === "moe" || t === "mlp" ? "none" : t))
      : lac
        ? Array.from({ length: L }, (_, i) => (lac.full_attn_layers.includes(i + 1) ? "full_attention" : "kda"))
        // Unlimited-OCR (Baidu) : décodeur de DeepSeek-OCR, toutes ses couches en fenêtre glissante de sliding_window
        // tokens (SlidingWindowLlamaAttention de modeling_deepseekv2.py, tampon circulaire au décodage).
        : c.model_type === "unlimited_ocr" && c.sliding_window
          ? Array<string>(L).fill("sliding_attention")
        // Gemma 3 (sans layer_types) : une couche globale toutes les sliding_window_pattern, les autres en fenêtre
        // (modeling_gemma3.py : is_sliding = (layer_idx + 1) % pattern != 0) ; 34 couches de Gemma 3 4B : 5 globales.
        : c.sliding_window_pattern && c.sliding_window
          ? Array.from({ length: L }, (_, i) => ((i + 1) % c.sliding_window_pattern! === 0 ? "full_attention" : "sliding_attention"))
        // Gemma 2 : une couche sur deux en fenêtre, en commençant par la première (modeling_gemma2.py : is_sliding = not layer_idx % 2).
        : c.model_type === "gemma2" && c.sliding_window
          ? Array.from({ length: L }, (_, i) => (i % 2 === 0 ? "sliding_attention" : "full_attention"))
          : Array<string>(L).fill("full_attention"));
  const count = (t: string) => types.filter((x) => x === t).length;
  const sliding = count("sliding_attention"), linear = count("linear_attention"), mamba = count("mamba"), kda = count("kda");
  const conv = count("conv"); // LFM2 : pas de KV, un petit état de convolution
  const unknown = types.length - count("full_attention") - sliding - linear - mamba - kda - conv - count("none");
  const full = count("full_attention") + unknown;

  const kv: KvGroup[] = [];
  const arch = [`${L} layers`];
  let state = 0;

  if (c.compress_ratios && c.kv_source_layer_ids) {
    // DeepSeek V4.1 (CSA2) : seules les couches sources écrivent le KV compressé et les
    // clés d'indexeur, les autres les relisent ; fenêtre glissante sur toutes les couches.
    const win = c.sliding_window ?? 128;
    const src = c.kv_source_layer_ids.filter((l) => l < L);
    const idx = new Set(c.index_source_layer_ids ?? src);
    kv.push({ layers: L, width: headDim, window: win, fp8Bytes: DSV4_FP8_BYTES });
    for (const r of new Set(src.map((l) => c.compress_ratios![l]))) {
      const at = src.filter((l) => c.compress_ratios![l] === r);
      const ratio = r > 1 ? { ratio: r } : {};
      const own = at.filter((l) => idx.has(l)).length;
      kv.push({ layers: at.length, width: headDim, ...ratio, fp8Bytes: DSV4_FP8_BYTES });
      if (own) kv.push({ layers: own, width: c.index_head_dim ?? 128, ...ratio, fixedBytes: DSA_INDEXER_BYTES(c.index_head_dim ?? 128) });
      if (r > 1) state += csa2State(at.length, r, headDim);
    }
    arch.push(`shared ${headDim}-wide KV written by ${src.length} of ${L} layers, ${win}-token window`);
  } else if (c.compress_ratios) {
    // DeepSeek V4 : fenêtre sur toutes les couches + séquence compressée 4× / 128×.
    const r = c.compress_ratios.slice(0, L);
    const r4 = r.filter((x) => x === 4).length, r128 = r.filter((x) => x === 128).length;
    kv.push(
      { layers: L, width: headDim, window: c.sliding_window ?? 128, fp8Bytes: DSV4_FP8_BYTES },
      { layers: r4, width: headDim, ratio: 4, fp8Bytes: DSV4_FP8_BYTES },
      { layers: r128, width: headDim, ratio: 128, fp8Bytes: DSV4_FP8_BYTES },
      { layers: r4, width: c.index_head_dim ?? 128, ratio: 4, fixedBytes: DSA_INDEXER_BYTES(c.index_head_dim ?? 128) },
    );
    state += dsv4State(r4, r128);
    arch.push(`${headDim}-wide latent, ${c.sliding_window ?? 128}-token window + compression (${r4} layers 4×, ${r128} layers 128×)`);
  } else if (c.kv_lora_rank) {
    // MLA clairsemé (indexeur DSA) : format FP8 compact de vLLM ; MLA dense : FP8 par tenseur, 1 octet.
    kv.push({ ...mla(full, c.kv_lora_rank, c.qk_rope_head_dim ?? 64), ...(c.index_head_dim ? { fp8Bytes: SPARSE_MLA_FP8_BYTES } : {}) });
    arch.push(`MLA (${c.kv_lora_rank} + ${c.qk_rope_head_dim ?? 64} latent) on ${full}`);
    if (c.index_head_dim) {
      // Sparse attention (DSA) : clés d'indexeur des seules couches d'attention, partagées entre
      // couches sur GLM-5, regroupées par blocs de tokens sur GLM-5.3-Flash (index_kpool).
      const own = c.indexer_types ? c.indexer_types.filter((t, i) => t === "full" && types[i] === "full_attention").length : full;
      const pool = c.index_kpool_compress && c.index_kpool && c.index_kpool > 1 ? { ratio: c.index_kpool } : {};
      kv.push({ layers: own, width: c.index_head_dim, ...pool, fixedBytes: DSA_INDEXER_BYTES(c.index_head_dim) });
      arch.push(`indexer keys on ${own}`);
    }
  } else {
    const gKv = c.num_global_key_value_heads ?? kvHeads, gDim = c.global_head_dim ?? headDim;
    if (full) {
      kv.push(gqa(full, gKv, gDim, undefined, c.v_head_dim ?? gDim));
      arch.push(`${full} full attention (${gKv} KV × ${gDim}${c.v_head_dim && c.v_head_dim !== gDim ? `, values ${c.v_head_dim}` : ""})`);
      // Qwen Sparse Attention : une clé d'indexeur par bloc de tokens, en plus du KV complet.
      if (c.indexer_head_dim) {
        const r = c.indexer_compress_ratio ?? 1;
        // Clés compressées en BF16 dans vLLM, quel que soit le format du cache principal.
        const w = (c.indexer_kv_heads ?? 1) * c.indexer_head_dim;
        kv.push({ layers: full, width: w, ...(r > 1 ? { ratio: r } : {}), fixedBytes: w * 2 });
        arch.push(`indexer keys per ${r}-token block`);
      }
      // Step-5 (attention clairsemée, csa_block_compress) : le KV complet, plus un résumé FP8 de proxy_dim
      // octets par bloc de region_block_size tokens (DSALayerCache, inference/kernel.py du dépôt).
      const sp = c.sparse_config;
      if (sp?.enabled && sp.compression_method === "csa_block_compress" && sp.proxy_dim) {
        const r = sp.region_block_size ?? 8;
        kv.push({ layers: full, width: sp.proxy_dim, ratio: r, fixedBytes: sp.proxy_dim });
        arch.push(`sparse attention, a summary per ${r}-token block`);
      }
      // MiniMax M3 (MiniMax Sparse Attention) : le KV complet, plus une clé d'index de sparse_index_dim valeurs par token,
      // en BF16 dans vLLM quel que soit le format du cache (MiniMaxM3IndexerCache, vllm/models/minimax_m3/common/indexer.py).
      const msa = c.sparse_attention_config;
      if (msa?.use_sparse_attention && msa.sparse_index_dim) {
        const n = msa.sparse_attention_freq ? msa.sparse_attention_freq.slice(0, L).filter((x) => x === 1).length : full;
        if (n) {
          kv.push({ layers: n, width: msa.sparse_index_dim, fixedBytes: msa.sparse_index_dim * 2 });
          arch.push(`sparse attention, ${msa.sparse_index_dim}-dim index keys on ${n}`);
        }
      }
    }
    if (sliding) {
      const window = c.sliding_window ?? c.attention_chunk_size ?? undefined;
      const sKv = c.swa_num_key_value_heads ?? kvHeads, sDim = c.swa_head_dim ?? headDim;
      kv.push(gqa(sliding, sKv, sDim, window, c.swa_v_head_dim ?? c.v_head_dim ?? sDim));
      arch.push(`${sliding} ${c.attention_chunk_size && !c.sliding_window ? "chunked-attention" : "sliding-window"} layers (${window ?? "?"} tokens, ${sKv} KV × ${sDim})`);
    }
  }

  if (linear) {
    if (c.linear_num_value_heads) {
      state += gdnState(linear, c.linear_num_key_heads ?? 0, c.linear_num_value_heads,
        c.linear_key_head_dim ?? 0, c.linear_value_head_dim ?? 0, c.linear_conv_kernel_dim ?? 4);
    }
    arch.push(`${linear} linear attention`);
  }
  if (mamba) {
    state += mamba2State(mamba, c.mamba_num_heads ?? 0, c.mamba_head_dim ?? 0, c.ssm_state_size ?? 0, c.n_groups ?? 1, c.conv_kernel ?? 4);
    arch.push(`${mamba} Mamba-2`);
  }
  if (kda && lac) {
    state += kdaState(kda, lac.num_heads, lac.head_dim, lac.short_conv_kernel_size);
    arch.push(`${kda} Kimi Delta Attention`);
  }
  if (conv) {
    state += convState(conv, c.conv_dim ?? hidden, c.conv_L_cache ?? 3);
    arch.push(`${conv} short-convolution layers`);
  }
  if (unknown) arch.push(`${unknown} unrecognised layers counted as full attention`);
  // Qwen3 d'origine (avril 2025) : 40 960 tokens dans config.json, 131 072 avec YaRN (facteur 4 sur 32 768), comme
  // le disent leurs fiches officielles ; la commande l'active au-delà (deploy.ts). Qwen3.5 et suivants : 262 144 natifs.
  const yarn = (c.model_type === "qwen3" || c.model_type === "qwen3_moe") && c.max_position_embeddings === 40960 && !c.rope_scaling
    ? { factor: 4, original: 32768, native: 40960 } : undefined;
  if (yarn) arch.push("32K native, 128K with YaRN");
  // Un modèle Mamba dont aucune couche Mamba n'a été trouvée : son motif de couches n'a pas été lu
  // (Nemotron 3.5 Lightning, publié avec 52 couches d'attention au lieu de 6). Jamais publié tel quel.
  if (!mamba && c.mamba_num_heads && c.ssm_state_size) arch.push("layer pattern unrecognised: Mamba-2 layers counted as full attention");

  // Checkpoint pré-quantifié : taille exacte des fichiers (hors sous-dossiers comme original/).
  const quant = raw.quantization_config ?? c.quantization_config;
  const fmt = (q: NonNullable<HfConfig["quantization_config"]>) => {
    const f = quantFormat(q);
    return { label: `${f.label} (native)`, ...(f.needs ? { needs: f.needs } : {}) };
  };
  // Cache FP8 par défaut : échelles calibrées dans le checkpoint, ou format imposé par vLLM (DeepSeek V4).
  const kvFp8 = quant?.kv_cache_quant_algo === "FP8" || (quant?.kv_cache_scheme?.type === "float" && quant.kv_cache_scheme.num_bits === 8) || !!c.compress_ratios;
  const fileBytes = (api.siblings ?? [])
    .filter((s) => s.rfilename.endsWith(".safetensors") && !s.rfilename.includes("/"))
    .reduce((sum, s) => sum + (s.size ?? 0), 0);

  // Dernier rempart : un modèle mal compris doit donner un message, jamais des NaN.
  const vocab = c.vocab_size ?? c.padded_vocab_size ?? 0;
  const finite = (x: number) => Number.isFinite(x) && x >= 0;
  if (!finite(state) || !kv.length || !kv.every((g) => finite(g.layers) && g.width > 0 && finite(g.width) && g.layers <= L)) {
    throw new Error("This model's architecture is not supported by the calculator yet. Try the closest preset.");
  }

  const activeParams = moeActiveParams(repo, c, L, hidden, params);
  const nExperts = c.n_routed_experts ?? c.num_experts ?? c.num_local_experts ?? c.moe_num_experts;
  const topK = c.num_experts_per_tok ?? c.experts_per_token ?? c.num_experts_per_token ?? c.top_k_experts;
  // Dimension interne d'un expert (gpt-oss n'a que intermediate_size) : l'arrondi des MoE FP4 de vLLM en dépend.
  // Hy-Memory (hy_v3) : expert_hidden_dim (768, forme des tenseurs) ; son moe_intermediate_size vaut 1 536.
  const expertFfn = c.expert_hidden_dim ?? c.moe_intermediate_size ?? (nExperts ? c.intermediate_size : undefined) ?? undefined;
  // Embeddings partagés : déclarés, ou par défaut chez LFM2 (sa config n'écrit pas la clé, et ses
  // poids n'ont pas de lm_head). Ailleurs, sans la clé : deux tableaux, le cas le plus lourd.
  const tied = raw.tie_word_embeddings ?? c.tie_word_embeddings ?? raw.tie_embedding ?? (/^lfm2/.test(raw.model_type ?? "") || undefined);
  return {
    id: `hf:${repo}`, label: repo, group: "Hugging Face",
    params, ...(activeParams ? { activeParams } : {}), hidden, layers: L, vocab, maxContext: yarn ? yarn.original * yarn.factor : c.max_position_embeddings ?? Infinity, ...(yarn ? { yarn } : {}),
    ...(activeParams && nExperts && topK ? { routing: { experts: nExperts, active: topK, ...(expertFfn ? { ffn: expertFfn } : {}) } } : {}),
    kv, ...(state ? { stateBytesPerSeq: state } : {}),
    ...(quant && fileBytes ? { native: { ...fmt(quant), bytes: fileBytes } } : {}),
    ...(kvFp8 ? { fp8Kv: true } : {}),
    ...(tied ? { tiedEmbeddings: true } : {}),
    arch: arch.join(" · "),
    repo,
  };
}
